/**
 * The world transforms of the nodes a gizmo reads at live paths, as the previewer draws them: the
 * object `NodeDispatcher` mounts for each path, with its ancestors, instances and GLB frames. A
 * path whose node is not mounted, or is no Node3D, reads as null.
 */
import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { sameTransform3D } from '../../godot/math/transform3d.js';
import { trackTargetFinder } from '../animation/trackTargets.js';
import { useOptionalSelection } from '../contexts/SelectionContext.js';
import { realGlobalTransform3D } from '../nodeTreeTransforms.js';
function sameTransforms(a, b) {
    return a.length === b.length && a.every((transform, i) => sameOptionalTransform(transform, b[i]));
}
function sameOptionalTransform(a, b) {
    return a === null || b === null ? a === b : sameTransform3D(a, b);
}
function isDetached(object) {
    return object !== null && object.parent === null;
}
/**
 * `previous` while it holds: the same paths, the registry no larger or smaller, every object still
 * mounted. A lookup walks every registered wrapper, and a path with no node would repeat it each frame.
 */
function lookupObjects(registry, paths, previous) {
    const holds = previous !== null &&
        previous.registrySize === registry.size &&
        previous.paths.length === paths.length &&
        previous.paths.every((path, i) => path === paths[i]) &&
        !previous.objects.some(isDetached);
    if (holds)
        return previous;
    const find = trackTargetFinder(registry);
    return {
        paths,
        registrySize: registry.size,
        objects: paths.map((path) => (path === null ? null : find(path))),
    };
}
/**
 * The world transform of the node at each live path, null for a path that names nothing drawn.
 * The transforms re-read every frame, since an animation moves a node with no render of its owner,
 * and the hook sets state only when one changes.
 */
export function useDrawnWorldTransforms(paths) {
    const registry = useOptionalSelection()?.nodeObjectMap ?? null;
    const [transforms, setTransforms] = useState([]);
    const lookup = useRef(null);
    // The world matrix each transform was read at, so a frame where nothing moved builds no transform.
    const read = useRef(null);
    useFrame(() => {
        if (registry === null)
            return;
        lookup.current = lookupObjects(registry, paths, lookup.current);
        const { objects } = lookup.current;
        if (read.current?.objects !== objects) {
            read.current = { objects, elements: objects.map(() => null), transforms: objects.map(() => null) };
        }
        const { elements, transforms: readTransforms } = read.current;
        let hasMoved = false;
        objects.forEach((object, i) => {
            if (object === null)
                return;
            object.updateWorldMatrix(true, false);
            const now = object.matrixWorld.elements;
            if (elements[i]?.every((value, j) => value === now[j]))
                return;
            elements[i] = [...now];
            readTransforms[i] = realGlobalTransform3D(object);
            hasMoved = true;
        });
        if (hasMoved || transforms.length !== objects.length) {
            const next = [...readTransforms];
            setTransforms((previous) => (sameTransforms(previous, next) ? previous : next));
        }
    });
    return transforms;
}
//# sourceMappingURL=useDrawnWorldTransforms.js.map